// Brex embed helpers for code that can't import the host's brex/bridge.ts (e.g. the auth
// microfrontend). The host persists `?isBrexApp=true` under this key on load.
const IS_BREX_APP_KEY = "cobt-is-brex-app";
const HOST_MESSAGE_EVENT = "host:message";

export const BREX_MAX_AUTH_RETRIES = 2;
// How long to wait for Brex to post a fresh LAUNCH_CODE after AUTH_FAILED.
export const BREX_RELAUNCH_WAIT_MS = 15000;

// Re-launch state is per document and lives on `window` so the host bundle and the auth
// microfrontend (separately bundled copies of this module) share it. The unauthorized page
// can mount more than once per failure, so this keeps each attempt to one AUTH_FAILED. A new
// iframe document (Brex remount / reload) starts clean.
interface BrexRelaunchState {
  retries: number;
  pendingAt: number | null;
  failed: boolean;
}

const getState = (): BrexRelaunchState => {
  const holder = window as unknown as {
    __cobtBrexRelaunch?: BrexRelaunchState;
  };
  if (holder.__cobtBrexRelaunch == null) {
    holder.__cobtBrexRelaunch = { retries: 0, pendingAt: null, failed: false };
  }
  return holder.__cobtBrexRelaunch;
};

const safeGet = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

// True when running as the Brex app inside a parent frame.
export const isEmbeddedInBrex = (): boolean => {
  const fromUrl =
    new URLSearchParams(window.location.search).get("isBrexApp") === "true";
  const isBrexApp = fromUrl || safeGet(IS_BREX_APP_KEY) === "true";
  return isBrexApp && window.parent != null && window.parent !== window;
};

// Tells the Brex host the session is unusable. Routed through the host's `host:message`
// relay (brex/bridge.ts), which posts ERROR to the Brex parent; Brex responds to
// retryable AUTH_FAILED by posting a fresh LAUNCH_CODE.
export const notifyBrexAuthFailed = (retryable: boolean): void => {
  window.dispatchEvent(
    new CustomEvent(HOST_MESSAGE_EVENT, {
      detail: { type: "ERROR", payload: { code: "AUTH_FAILED", retryable } },
    })
  );
};

export type BrexRelaunchStep =
  | { kind: "wait"; remainingMs: number }
  | { kind: "failed" };

// Gives up: tells Brex (once) that retrying won't help.
export const failBrexRelaunch = (): void => {
  const state = getState();
  state.pendingAt = null;
  if (state.failed) {
    return;
  }
  state.failed = true;
  notifyBrexAuthFailed(false);
};

// Asks Brex for a fresh launch code, or keeps waiting on a request already in flight.
export const startBrexRelaunch = (
  now: number = Date.now()
): BrexRelaunchStep => {
  const state = getState();
  if (state.failed) {
    return { kind: "failed" };
  }
  if (
    state.pendingAt != null &&
    now - state.pendingAt < BREX_RELAUNCH_WAIT_MS
  ) {
    return {
      kind: "wait",
      remainingMs: BREX_RELAUNCH_WAIT_MS - (now - state.pendingAt),
    };
  }
  if (state.retries >= BREX_MAX_AUTH_RETRIES) {
    failBrexRelaunch();
    return { kind: "failed" };
  }
  state.retries += 1;
  state.pendingAt = now;
  notifyBrexAuthFailed(true);
  return { kind: "wait", remainingMs: BREX_RELAUNCH_WAIT_MS };
};

// A new launch code arrived: the pending request is answered, so a later failure may ask again.
export const clearBrexRelaunchPending = (): void => {
  getState().pendingAt = null;
};

// Successful login: start over.
export const resetBrexAuthRetries = (): void => {
  const state = getState();
  state.retries = 0;
  state.pendingAt = null;
  state.failed = false;
};
